'use client';

import { motion } from 'framer-motion';

import { Image } from '@/components/client';

interface GoodsImage {
  url: string;
  alt: string;
}
interface GoodsProps {
  title: string;
  description: string;
  badgeText: string;
  goodsText: string;
  images: GoodsImage[];
}

// Premium easing curves
const EASE = {
  smooth: [0.6, 0.01, 0.05, 0.95] as const,
  spring: [0.34, 1.56, 0.64, 1] as const,
  elegant: [0.16, 1, 0.3, 1] as const,
};

const ITEM_ANIMATION = {
  hidden: {
    opacity: 0,
    y: 50,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: EASE.elegant,
    },
  },
};

const TEXT_ANIMATION = {
  hidden: {
    opacity: 0,
    y: 20,
    filter: 'blur(8px)',
  },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.6,
      ease: EASE.smooth,
      delay: 0.1,
    },
  },
};

const BOX_ANIMATION = {
  hidden: {
    opacity: 0,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: EASE.spring,
      delay: 0.2,
    },
  },
};

const BADGE_ANIMATION = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    rotate: -10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      duration: 0.5,
      ease: EASE.spring,
      delay: 0.4,
    },
  },
};

export const Goods = ({
  title,
  description,
  badgeText,
  images,
  goodsText,
}: GoodsProps) => {
  return (
    <motion.li
      variants={ITEM_ANIMATION}
      className="relative flex w-full flex-col content-stretch items-center"
    >
      <motion.div
        variants={TEXT_ANIMATION}
        className="flex w-full flex-col content-stretch items-start gap-12 text-Gray-white"
      >
        <h2 className="w-full whitespace-pre-wrap font-800 typo-h4">{title}</h2>
        <p className="w-full whitespace-pre-wrap font-600 typo-body3">
          {description}
        </p>
      </motion.div>

      <motion.div
        variants={BOX_ANIMATION}
        whileHover={{
          scale: 1.02,
          y: -4,
          transition: { duration: 0.3, ease: EASE.smooth },
        }}
        className="relative mt-32 box-border flex w-full flex-col content-stretch text-clip rounded-xlarge bg-[#28292E]"
      >
        {images.map(({ alt, url }) => (
          <div key={url} className="relative aspect-[311/255] w-full">
            <Image
              alt={alt}
              src={url}
              fill
              sizes="(max-width: 850px) 100vw, 500px"
            />
          </div>
        ))}

        <motion.div
          variants={BADGE_ANIMATION}
          whileHover={{
            scale: 1.1,
            rotate: 5,
            transition: { duration: 0.2, ease: EASE.spring },
          }}
          whileTap={{ scale: 0.95 }}
          className="absolute right-10 top-6 box-border flex size-70 content-stretch items-center justify-center gap-8 rounded-pill bg-B-500 text-Gray-white"
        >
          <p className="text-white absolute left-1/2 top-1/2 w-full -translate-x-1/2 -translate-y-1/2 whitespace-pre-wrap text-center font-700 uppercase !leading-[120%] typo-body3">
            {badgeText}
          </p>
        </motion.div>

        <p className="absolute bottom-20 left-20 whitespace-nowrap font-700 text-Gray-white typo-body3">
          {goodsText}
        </p>
      </motion.div>

      <p className="mt-12 w-full text-left font-500 text-Gray-500 typo-body4">
        *증정 수량 소진 시 조기 종료될 수 있습니다.
      </p>
    </motion.li>
  );
};
